import { Injectable } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';
import { Universe } from '../../interfaces/universe';
import { Saga } from '../../interfaces/saga';
import { BookSimple } from '../../interfaces/book';
import { Antology } from '../../interfaces/antology';
import { Author } from '../../interfaces/author';

@Injectable({
    providedIn: 'root'
})
export class UniverseStoreService {
    universoVacio: Universe = {
        Id: 0,
        Nombre: '',
        Autores: [],
        Sagas: [],
        Libros: [],
        Antologias: []
    };
    sagaVacia: Saga = {
        Id: 0,
        Nombre: '',
        Subtitulo: null,
        Autores: [],
        Libros: [],
        Antologias: []
    }
    libroVacio: BookSimple = {
        Id: 0,
        Nombre: '',
        Autores: [],
        Estados: [],
        Orden: -1,
        Portada: ''
    }
    
    private universesLoadedSubject = new BehaviorSubject<boolean>(false);
    private universesSubject = new BehaviorSubject<Universe[]>([]);
    universesLoaded$ = this.universesLoadedSubject.asObservable();
    universes$ = this.universesSubject.asObservable();
    sagas$ = this.universes$.pipe(
        map(universes => universes.flatMap(u => u.Sagas || []))
    );

    setUniverses(universes: Universe[]) {
        this.universesLoadedSubject.next(true);
        this.universesSubject.next(universes);
    }

    hasLoadedUniverses(): boolean {
        return this.universesLoadedSubject.getValue();
    }

    getUniverses(): Universe[] {
        return this.universesSubject.getValue();
    }

    getAllSagas(): Saga[] {
        const allSagas = this.getUniverses()
            .flatMap(u => u.Sagas || []);
        return allSagas;
    }

    getAllBooks(): BookSimple[] {
        const booksFromUniverses = this.getUniverses()
            .flatMap(u => u.Libros || []);
        const booksFromSagas = this.getUniverses()
            .flatMap(u => u.Sagas || [])
            .flatMap(s => s.Libros || []);
        return [...booksFromUniverses, ...booksFromSagas];
    }

    getAllAnthologies(): Antology[] {
        const antologiesFromUniverses = this.getUniverses()
            .flatMap(u => u.Antologias || []);
        const antologiesFromSagas = this.getUniverses()
            .flatMap(u => u.Sagas || [])
            .flatMap(s => s.Antologias || []);
        return [...antologiesFromUniverses, ...antologiesFromSagas];
    }

    getUniverseById(id: number | string): Universe {
        const universeId = Number(id);
        const universoEncontrado = this.getUniverses().find(u => u.Id === universeId);
        return universoEncontrado ?? this.universoVacio;
    }

    getUniverse(nombre: string): Universe | undefined {
        const universoEncontrado = this.getUniverses().find(u => u.Nombre === nombre);
        return universoEncontrado;
    }

    getSagaById(id: number): Saga {
        const sagaEncontrada = this.getAllSagas().find(s => s.Id === id);
        return sagaEncontrada ?? this.sagaVacia;
    }

    getSaga(nombre: string): Saga | undefined {
        const sagaEncontrada = this.getAllSagas().find(u => u.Nombre === nombre);
        if (sagaEncontrada) {
            return sagaEncontrada;
        }
        return;
    }

    getUniverseOfSaga(sagaId: number): Universe | null {
        for (const universe of this.getUniverses()) {
            if (universe.Sagas?.some(saga => saga.Id === sagaId)) {
                return universe;
            }
        }
        return null;
    }

    getUniverseOfBook(bookId: number): Universe | null {
        for (const universe of this.getUniverses()) {
            if (universe.Libros?.some(l => l.Id === bookId)) {
                return universe;
            }

            for (const saga of universe.Sagas || []) {
                if (saga.Libros?.some(l => l.Id === bookId)) {
                    return universe;
                }
            }
        }
        return null;
    }

    getUniverseOfAntology(antologyId: number): Universe | null {
        for (const universe of this.getUniverses()) {
            if (universe.Antologias?.some(a => a.Id === antologyId)) {
                return universe;
            }

            for (const saga of universe.Sagas || []) {
                if (saga.Antologias?.some(a => a.Id === antologyId)) {
                    return universe;
                }
            }
        }
        return null;
    }

    getSagaOfBook(bookId: number): Saga | null {
        for (const universe of this.getUniverses()) {
            for (const saga of universe.Sagas || []) {
                if (saga.Libros?.some(l => l.Id === bookId)) {
                    return saga;
                }
            }
        }
        return null;
    }

    getSagaOfAntology(antologyId: number): Saga | null {
        for (const universe of this.getUniverses()) {
            for (const saga of universe.Sagas || []) {
                if (saga.Antologias?.some(a => a.Id === antologyId)) {
                    return saga;
                }
            }
        }
        return null;
    }

    getBookById(bookId: number): BookSimple {
        const books = this.getAllBooks();
        for (const book of books) {
            if(book.Id === bookId)
                return book;
        }
        return this.libroVacio;
    }

    getAntologyById(antologyId: number): BookSimple {
        const antologies = this.getAllAnthologies();
        for (const antology of antologies) {
            if(antology.Id === antologyId)
                return antology;
        }
        return this.libroVacio;
    }

    clear() {
        this.universesLoadedSubject.next(false);
        this.universesSubject.next([]);
    }

    addUniverse(newUniverse: Universe): void {
        const current = this.getUniverses();
        const exists = current.some(u => u.Id === newUniverse.Id);

        if (!exists) {
            this.universesSubject.next([...current, newUniverse]);
        }
    }

    addSaga(newSaga: Saga, universe: Universe): void {
        const universosActuales = this.getUniverses();
        const current = this.getAllSagas();
        const exists = current.some(s => s.Id === newSaga.Id);

        if (!exists) {
            universe.Sagas = [...universe.Sagas, newSaga];
            this.universesSubject.next([...universosActuales]);
        }
    }

    addAntology(newAntology: Antology, universe: Universe, saga: Saga): void {
        const universosActuales = this.getUniverses();
        const current = this.getAllAnthologies();
        const exists = current.some(a => a.Id === newAntology.Id);

        if (exists)
            return;

        if (newAntology.Orden > -1) {
            saga.Antologias = [...saga.Antologias, newAntology];
        } else
            universe.Antologias = [...universe.Antologias, newAntology];
        this.universesSubject.next([...universosActuales]);
    }

    addBook(newBook: BookSimple, universe: Universe, saga: Saga): void {
        const universosActuales = this.getUniverses();
        const current = this.getAllBooks();
        const exists = current.some(b => b.Id === newBook.Id);

        if (exists)
            return;

        if (newBook.Orden > -1) {
            saga.Libros = [...saga.Libros, newBook];
        } else
            universe.Libros = [...universe.Libros, newBook];
        this.universesSubject.next([...universosActuales]);
    }

    updateAuthor(author: Author): void {
        const updatedUniverses = this.getUniverses().map(u => ({
            ...u,
            Autores: u.Autores?.map(a => a.Id === author.Id ? { ...a, ...author } : a) || [],
            Sagas: u.Sagas?.map(s => ({
                ...s,
                Autores: s.Autores?.map(a => a.Id === author.Id ? { ...a, ...author } : a) || [],
                Libros: s.Libros?.map(l => ({
                    ...l,
                    Autores: l.Autores?.map(a => a.Id === author.Id ? { ...a, ...author } : a) || []
                })) || [],
                Antologias: s.Antologias?.map(a => ({
                    ...a,
                    Autores: a.Autores?.map(authorItem => authorItem.Id === author.Id ? { ...authorItem, ...author } : authorItem) || []
                })) || []
            })) || [],
            Libros: u.Libros?.map(l => ({
                ...l,
                Autores: l.Autores?.map(a => a.Id === author.Id ? { ...a, ...author } : a) || []
            })) || [],
            Antologias: u.Antologias?.map(a => ({
                ...a,
                Autores: a.Autores?.map(authorItem => authorItem.Id === author.Id ? { ...authorItem, ...author } : authorItem) || []
            })) || []
        }));
        this.universesSubject.next([...updatedUniverses]);
    }
}
